import EarthIcon from '@src/svgs/EarthIcon';
import LinkedInCommentIcon from '@src/svgs/LinkedInCommentIcon';
import LinkedInLikeIcon from '@src/svgs/LinkedInLikeIcon';
import LinkedInRepostIcon from '@src/svgs/LinkedInRepostIcon';
import LinkedInSendIcon from '@src/svgs/LinkedInSendIcon';
import React from 'react';
import styled from 'styled-components';

import { htmlToPlainText } from './htmlToPlainText';

/** Distribution channel slug this preview renders (PLAT-11038). */
export const CHANNEL_SLUG = 'linkedin';

type LinkedInViewport = 'desktop' | 'mobile';
type AccountKind = 'person' | 'company';

type Props = {
  content: string;
  viewport?: LinkedInViewport;
  authorName?: string;
  avatarUrl?: string;
  accountKind?: AccountKind;
};

const ACTION_ICON_COLOR = '#666666';
const ACTION_ICON_STROKE = '3';

const LinkedInPreview: React.FC<Props> = ({
  content,
  viewport = 'desktop',
  authorName,
  avatarUrl,
  accountKind = 'company',
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [truncatedText, setTruncatedText] = React.useState<string | null>(null);
  const [overflows, setOverflows] = React.useState(false);
  const measureRef = React.useRef<HTMLDivElement>(null);

  const plainText = htmlToPlainText(content);

  const isMobile = viewport === 'mobile';
  const isCompany = accountKind === 'company';
  // Line budget includes 1 text line + 1 blank paragraph break + 1
  // truncated line of the next paragraph.
  const lineClamp = isMobile ? 2 : 3;
  const SEE_MORE_SUFFIX = '...more';

  React.useLayoutEffect(() => {
    if (isExpanded) return undefined;
    const el = measureRef.current;
    if (!el) return undefined;

    const measureAndTruncate = () => {
      const lineHeightPx = parseFloat(getComputedStyle(el).lineHeight);
      const maxHeight = lineHeightPx * lineClamp + 1;

      el.textContent = plainText;
      if (el.scrollHeight <= maxHeight) {
        setTruncatedText(plainText);
        setOverflows(false);
        el.textContent = '';
        return;
      }

      let lo = 0;
      let hi = plainText.length;
      let bestFit = 0;

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        el.textContent = plainText.substring(0, mid) + SEE_MORE_SUFFIX;
        if (el.scrollHeight <= maxHeight) {
          bestFit = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      // Trim trailing whitespace / newlines from the prefix so the
      // appended "...more" sits flush against the last visible
      // character (the leading "..." is the separator; no extra
      // space, no line break pushing the link to a new line).
      const trimmedPrefix = plainText.substring(0, bestFit).replace(/\s+$/, '');
      setTruncatedText(trimmedPrefix);
      setOverflows(true);
      el.textContent = '';
    };

    measureAndTruncate();

    const resizeObserver = new ResizeObserver(() => {
      measureAndTruncate();
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [plainText, lineClamp, isExpanded]);

  const actionIconSize = isMobile ? '13' : '15';

  return (
    <Container>
      <PreviewCard viewport={viewport}>
        <PostHeader isMobile={isMobile}>
          <Avatar isMobile={isMobile} isCompany={isCompany}>
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt={authorName || 'Author'} />
            ) : (
              <AvatarPlaceholder isCompany={isCompany} />
            )}
          </Avatar>
          <PostMeta>
            <AuthorName isMobile={isMobile}>
              {authorName || (isCompany ? 'Your Company' : 'Your Name')}
            </AuthorName>
            {!isCompany && (
              <PostInfo isMobile={isMobile}>Your Title • 1st</PostInfo>
            )}
            <PostTime isMobile={isMobile}>
              Now • <EarthIcon fillColor="#666666" />
            </PostTime>
          </PostMeta>
          <HeaderActions>
            {!isMobile && <MoreButton aria-label="More">•••</MoreButton>}
          </HeaderActions>
        </PostHeader>

        <PostContentWrapper isMobile={isMobile}>
          <MeasureNode ref={measureRef} isMobile={isMobile} aria-hidden />
          <PostContent
            isMobile={isMobile}
            clickable={!isExpanded && overflows}
            onClick={
              !isExpanded && overflows ? () => setIsExpanded(true) : undefined
            }
          >
            {isExpanded || truncatedText === null ? plainText : truncatedText}
            {!isExpanded && overflows && (
              <SeeMoreLink
                onClick={e => {
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
              >
                ...more
              </SeeMoreLink>
            )}
            {isExpanded && overflows && (
              <>
                {' '}
                <SeeLessLink
                  onClick={e => {
                    e.stopPropagation();
                    setIsExpanded(false);
                  }}
                >
                  ...less
                </SeeLessLink>
              </>
            )}
          </PostContent>
        </PostContentWrapper>

        <EngagementStats isMobile={isMobile}>
          <EngagementLeft>
            <ReactionIcons>
              <ReactionIcon bgColor="#0a66c2">👍</ReactionIcon>
              <ReactionIcon bgColor="#df704d">❤️</ReactionIcon>
              <ReactionIcon bgColor="#6dae4f">👏</ReactionIcon>
            </ReactionIcons>
            <EngagementCount>276</EngagementCount>
          </EngagementLeft>
          <EngagementRight>
            <EngagementItem>13 comments</EngagementItem>
            <EngagementDot>•</EngagementDot>
            <EngagementItem>38 reposts</EngagementItem>
          </EngagementRight>
        </EngagementStats>

        <ActionDivider isMobile={isMobile} />

        <PostActions isMobile={isMobile}>
          <ActionButton isMobile={isMobile}>
            <LinkedInLikeIcon
              width={actionIconSize}
              height={actionIconSize}
              strokeColor={ACTION_ICON_COLOR}
              strokeWidth={ACTION_ICON_STROKE}
            />
            <ActionText isMobile={isMobile}>Like</ActionText>
          </ActionButton>
          <ActionButton isMobile={isMobile}>
            <LinkedInCommentIcon
              width={actionIconSize}
              height={actionIconSize}
              strokeColor={ACTION_ICON_COLOR}
              strokeWidth={ACTION_ICON_STROKE}
            />
            <ActionText isMobile={isMobile}>Comment</ActionText>
          </ActionButton>
          <ActionButton isMobile={isMobile}>
            <LinkedInRepostIcon
              width={actionIconSize}
              height={actionIconSize}
              strokeColor={ACTION_ICON_COLOR}
              strokeWidth={ACTION_ICON_STROKE}
            />
            <ActionText isMobile={isMobile}>Repost</ActionText>
          </ActionButton>
          <ActionButton isMobile={isMobile}>
            <LinkedInSendIcon
              width={actionIconSize}
              height={actionIconSize}
              strokeColor={ACTION_ICON_COLOR}
              strokeWidth={ACTION_ICON_STROKE}
            />
            <ActionText isMobile={isMobile}>Send</ActionText>
          </ActionButton>
        </PostActions>
      </PreviewCard>

      <CharacterCount total={plainText.length}>
        {plainText.length.toLocaleString()} / 3,000 characters
      </CharacterCount>
    </Container>
  );
};

export default LinkedInPreview;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PreviewCard = styled.div<{ viewport: 'desktop' | 'mobile' }>`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: ${props => (props.viewport === 'mobile' ? '0' : '8px')};
  overflow: hidden;
  width: 100%;
  max-width: ${props => (props.viewport === 'mobile' ? '375px' : '552px')};
  margin: 0 auto;
  box-shadow: ${props =>
    props.viewport === 'mobile' ? '0 0 0 1px #e0e0e0' : 'none'};
`;

const PostHeader = styled.div<{ isMobile: boolean }>`
  display: flex;
  gap: ${props => (props.isMobile ? '10px' : '8px')};
  padding: ${props => (props.isMobile ? '12px' : '12px 16px')};
  position: relative;
`;

const Avatar = styled.div<{ isMobile: boolean; isCompany: boolean }>`
  width: ${props => (props.isMobile ? '40px' : '48px')};
  height: ${props => (props.isMobile ? '40px' : '48px')};
  border-radius: ${props => (props.isCompany ? '6px' : '50%')};
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarPlaceholder = styled.div<{ isCompany: boolean }>`
  width: 100%;
  height: 100%;
  background: ${props =>
    props.isCompany
      ? '#e7f3f9'
      : 'linear-gradient(135deg, #0a66c2 0%, #004182 100%)'};
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PostMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 10px;
  letter-spacing: 0.5px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
`;

const AuthorName = styled.div<{ isMobile: boolean }>`
  color: #000;
  font-size: ${props => (props.isMobile ? '13px' : '14px')};
  font-weight: 600;
`;

const PostInfo = styled.div<{ isMobile: boolean }>`
  color: #666;
  font-size: ${props => (props.isMobile ? '11px' : '12px')};
`;

const PostTime = styled.div<{ isMobile: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #666;
  font-size: ${props => (props.isMobile ? '11px' : '12px')};
`;

const PostContentWrapper = styled.div<{ isMobile: boolean }>`
  position: relative;
  padding: ${props =>
    props.isMobile ? '0 12px 12px 12px' : '0 16px 12px 16px'};
`;

const MeasureNode = styled.div<{ isMobile: boolean }>`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  top: 0;
  /* Inset by wrapper's padding so MeasureNode's text-width matches the
     visible PostContent's text-width (absolute children sit in the
     parent's padding-box, which is wider than the content area). */
  left: ${props => (props.isMobile ? '12px' : '16px')};
  right: ${props => (props.isMobile ? '12px' : '16px')};
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: ${props => (props.isMobile ? '13px' : '14px')};
  line-height: 1.42857;
`;

const PostContent = styled.div<{ isMobile: boolean; clickable: boolean }>`
  color: #000;
  font-size: ${props => (props.isMobile ? '13px' : '14px')};
  line-height: 1.42857;
  white-space: pre-wrap;
  word-wrap: break-word;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`;

const SeeMoreLink = styled.span`
  && {
    color: #8e8e8e;
  }
  font-weight: 400;
  cursor: pointer;

  &:hover {
    color: #0a66c2;
    text-decoration: underline;
  }
`;

const SeeLessLink = styled.span`
  && {
    color: #8e8e8e;
  }
  font-weight: 400;
  cursor: pointer;

  &:hover {
    color: #0a66c2;
    text-decoration: underline;
  }
`;

const EngagementStats = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => (props.isMobile ? '8px 12px 6px' : '8px 16px 6px')};
  font-size: ${props => (props.isMobile ? '11px' : '12px')};
  color: #888;
`;

const ActionDivider = styled.div<{ isMobile: boolean }>`
  height: 1px;
  background: #e0e0e0;
  margin-top: 1px;
  margin-right: ${props => (props.isMobile ? '12px' : '16px')};
  margin-left: ${props => (props.isMobile ? '12px' : '16px')};
  margin-bottom: 6px;
`;

const EngagementLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ReactionIcons = styled.div`
  display: flex;
  align-items: center;
  margin-right: 4px;
`;

const ReactionIcon = styled.span<{ bgColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.bgColor};
  border: 1.5px solid white;
  font-size: 9px;
  margin-left: -4px;

  &:first-child {
    margin-left: 0;
  }
`;

const EngagementCount = styled.span`
  color: #888;
  cursor: pointer;

  &:hover {
    color: #0a66c2;
    text-decoration: underline;
  }
`;

const EngagementRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EngagementItem = styled.span`
  cursor: pointer;

  &:hover {
    color: #0a66c2;
    text-decoration: underline;
  }
`;

const EngagementDot = styled.span`
  color: #888;
`;

const PostActions = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-around;
  padding: ${props => (props.isMobile ? '4px 4px' : '4px 8px')};
`;

const ActionButton = styled.button<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${props => (props.isMobile ? '4px' : '6px')};
  padding: ${props => (props.isMobile ? '4px 4px' : '6px 8px')};
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
  flex: 1;

  &:hover {
    background: #f3f6f8;
  }
`;

const ActionText = styled.span<{ isMobile: boolean }>`
  font-size: ${props => (props.isMobile ? '12px' : '14px')};
  color: #808080;
  font-weight: 500;
  line-height: 1;
`;

const CharacterCount = styled.div<{ total: number }>`
  text-align: right;
  font-size: 11px;
  color: ${props =>
    props.total > 3000
      ? '#d32f2f'
      : props.total > 2500
        ? '#f59e0b'
        : 'var(--text-muted)'};
  padding: 0 4px;
  font-weight: ${props => (props.total > 2500 ? '600' : '400')};
`;
